import * as THREE from 'three';
import * as BufferGeometryUtils from 'three/addons/utils/BufferGeometryUtils.js';
import { Water } from 'three/addons/objects/Water.js';
import { clearMarks, scorches } from './beam';
import { buildings, cityStatic, debris, fires } from './entities';
import { facadeSet, prepareFacades, propMat, roofMat, streetAO } from './facades';
import { clearGems } from './gems';
import { heliGroup } from './helis';
import { rebuildLabels } from './labels';
import { minimap } from './minimap';
import { world } from './physics';
import { TEX, scene, sunDir, texLoader, zeroM } from './scene';
import { ROAD_W, S } from './state';
import { buildTraffic } from './traffic';
import { $, clamp, rnd } from './util';
import { polyArea } from '../world/osm';

export let props = null, propCount = 0;
export function addBuilding(pts, h, def, tags, id?){
  const shape = new THREE.Shape(pts.map(p => new THREE.Vector2(p[0], -p[1])));
  const geo = new THREE.ExtrudeGeometry(shape, {depth:h, bevelEnabled:true, bevelThickness:0.5, bevelSize:0.35, bevelSegments:1});
  geo.rotateX(-Math.PI/2); geo.computeBoundingSphere(); geo.computeBoundingBox();
  const area = polyArea(pts), vol = area*h;
  const isGlass = h > 45 && Math.random() < def.glass;
  let wall, base;
  if (isGlass){
    const tint = new THREE.Color(def.glassTint).offsetHSL((Math.random()-.5)*0.04, 0, (Math.random()-.5)*0.08);
    wall = new THREE.MeshStandardMaterial({color:tint, metalness:0.85, roughness:0.18, envMapIntensity:1.3, emissive:new THREE.Color('#ffd7a0'), emissiveIntensity:0.06});
    base = tint.clone();
  } else {
    const f = facadeSet[(Math.random()*facadeSet.length)|0];
    wall = new THREE.MeshStandardMaterial({map:f.map, emissiveMap:f.em, emissive:new THREE.Color('#ffffff'), emissiveIntensity:0.55+Math.random()*0.35, roughness:0.85, metalness:0.02, envMapIntensity:0.35, bumpMap:f.map, bumpScale:0.6});
    base = new THREE.Color('#ffffff');
    const tc = tags && (tags['building:colour'] || tags['building:color']);
    if (tc){ try { const c = new THREE.Color(tc.trim()); base = c.lerp(new THREE.Color('#ffffff'), 0.35); wall.color.copy(base); } catch(e){} }
    else if (tags && /brick/.test(tags['building:material']||'')) { base = new THREE.Color('#b07a63'); wall.color.copy(base); }
  }
  streetAO(wall);
  const mesh = new THREE.Mesh(geo, [roofMat, wall]);
  mesh.castShadow = mesh.receiveShadow = true;
  const bb = geo.boundingBox;
  mesh.userData = { id, hp:1, maxHp:1, vol, pts, value:Math.round(vol*340), area, h, base, isGlass, emiss:wall.emissiveIntensity,
    center:bb.getCenter(new THREE.Vector3()), size:bb.getSize(new THREE.Vector3()), radius:geo.boundingSphere.radius, fr:Math.hypot(bb.max.x-bb.min.x, bb.max.z-bb.min.z)/2,
    hitT:-1, dirty:false, props:[], beacon:null, name:tags && tags.name || null };
  mesh.userData.maxHp = mesh.userData.hp = clamp(0.55 + vol/70000, 0.55, 7);
  mesh.scale.y = 0.02; mesh.userData.rise = Math.random()*-0.6;
  scene.add(mesh); buildings.push(mesh);
  return mesh;
}
export function addRoofProps(){
  const items = [];
  for (const b of buildings){
    const u = b.userData; if (u.area < 180 || Math.random() < 0.25) continue;
    const n = 1 + (Math.random()*3|0);
    for (let i=0;i<n;i++){
      const w = rnd(2.5,6), d = rnd(2.5,6), h = rnd(2,5);
      const x = u.center.x + (Math.random()-.5)*u.size.x*0.45, z = u.center.z + (Math.random()-.5)*u.size.z*0.45;
      items.push({b, x, y:u.h + h/2, z, w, h, d});
    }
  }
  if (props){ scene.remove(props); props.dispose(); props = null; }
  propCount = items.length; if (!propCount) return;
  props = new THREE.InstancedMesh(new THREE.BoxGeometry(1,1,1), propMat, propCount);
  props.castShadow = true; props.receiveShadow = true;
  const m = new THREE.Matrix4(), q = new THREE.Quaternion(), p = new THREE.Vector3(), s = new THREE.Vector3();
  items.forEach((it, i) => { p.set(it.x,it.y,it.z); s.set(it.w,it.h,it.d); m.compose(p,q,s); props.setMatrixAt(i, m); it.b.userData.props.push(i); });
  props.instanceMatrix.needsUpdate = true; scene.add(props);
}
export function hideProps(b){ if (!props) return; for (const i of b.userData.props) props.setMatrixAt(i, zeroM); props.instanceMatrix.needsUpdate = true; }
export function ribbon(pts, w, y){
  const geos = [];
  for (let i=0;i<pts.length-1;i++){
    const a = pts[i], b = pts[i+1]; const dx = b[0]-a[0], dz = b[1]-a[1]; const L = Math.hypot(dx,dz); if (L < 0.5) continue;
    const nx = -dz/L*w/2, nz = dx/L*w/2;
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute([a[0]+nx,y,a[1]+nz, b[0]+nx,y,b[1]+nz, b[0]-nx,y,b[1]-nz, a[0]-nx,y,a[1]-nz],3));
    g.setAttribute('normal', new THREE.Float32BufferAttribute([0,1,0, 0,1,0, 0,1,0, 0,1,0],3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute([0,0, 1,0, 1,1, 0,1],2));
    g.setIndex([0,2,1, 0,3,2]); geos.push(g);
    const disc = new THREE.CircleGeometry(w/2, 8); disc.rotateX(-Math.PI/2); disc.translate(b[0], y, b[1]); geos.push(disc);
  }
  if (pts.length){ const disc = new THREE.CircleGeometry(w/2, 8); disc.rotateX(-Math.PI/2); disc.translate(pts[0][0], y, pts[0][1]); geos.push(disc); }
  return geos;
}
export let lamps = null, lampHeads = null, trees = null, treeTops = null, waterMesh = null, waterGeoXY = null, waterStd = null;
export const lampMat = new THREE.MeshStandardMaterial({color:'#2a2a30', roughness:.6, metalness:.7});
export const lampHeadMat = new THREE.MeshBasicMaterial({color:new THREE.Color(2.4,1.5,0.7), fog:false});
export const trunkMat = new THREE.MeshStandardMaterial({color:'#4a3728', roughness:1});
export const canopyMat = new THREE.MeshStandardMaterial({color:'#2f5a34', roughness:.95, flatShading:true});
export function pip(pt, poly){ let c=false; for (let i=0,j=poly.length-1;i<poly.length;j=i++){ const xi=poly[i][0],zi=poly[i][1],xj=poly[j][0],zj=poly[j][1]; if (((zi>pt[1])!==(zj>pt[1])) && (pt[0] < (xj-xi)*(pt[1]-zi)/(zj-zi)+xi)) c=!c; } return c; }
export function buildStreetFurniture(data){
  for (const m of [lamps, lampHeads, trees, treeTops]) if (m){ scene.remove(m); m.dispose(); }
  lamps = lampHeads = trees = treeTops = null;
  const L = [], T = [];
  for (const r of data.roads){ const w = ROAD_W[r.type]||6; if (w < 7) continue; const every = w >= 12 ? 26 : 34;
    let acc = every*0.5; for (let i=0;i<r.pts.length-1;i++){ const a = r.pts[i], b = r.pts[i+1]; const dx=b[0]-a[0], dz=b[1]-a[1]; const len = Math.hypot(dx,dz)||1; const nx=-dz/len, nz=dx/len;
      for (let d=acc; d<len; d+=every){ const x = a[0]+dx*d/len, z = a[1]+dz*d/len; const off = w/2+1.6; L.push([x+nx*off, z+nz*off]); L.push([x-nx*off, z-nz*off]); if (w < 12 && Math.random()<0.5) T.push([x+nx*(off+2.5)*(Math.random()<.5?1:-1), z+nz*(off+2.5)*(Math.random()<.5?1:-1)]); }
      acc = ((acc - len) % every + every) % every; } }
  for (const poly of data.parks){ if (poly.length<3) continue; let minx=1e9,maxx=-1e9,minz=1e9,maxz=-1e9; for (const p of poly){ minx=Math.min(minx,p[0]); maxx=Math.max(maxx,p[0]); minz=Math.min(minz,p[1]); maxz=Math.max(maxz,p[1]); }
    const n = Math.min(220, Math.round(polyArea(poly)/260)); let tries = 0; let placed = 0;
    while (placed < n && tries++ < n*6){ const p = [rnd(minx,maxx), rnd(minz,maxz)]; if (pip(p, poly)){ T.push(p); placed++; } } }
  const M = new THREE.Matrix4(), Q = new THREE.Quaternion(), Pv = new THREE.Vector3(), Sv = new THREE.Vector3();
  if (L.length){
    lamps = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.18, 0.28, 9, 6), lampMat, L.length); lampHeads = new THREE.InstancedMesh(new THREE.SphereGeometry(0.55, 8, 6), lampHeadMat, L.length);
    L.forEach((p, i) => { Pv.set(p[0], 4.5, p[1]); M.compose(Pv, Q, Sv.set(1,1,1)); lamps.setMatrixAt(i, M); Pv.y = 9.2; M.compose(Pv, Q, Sv); lampHeads.setMatrixAt(i, M); });
    lamps.castShadow = true; scene.add(lamps); scene.add(lampHeads); }
  if (T.length){
    trees = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.35, 0.5, 4, 6), trunkMat, T.length); treeTops = new THREE.InstancedMesh(new THREE.IcosahedronGeometry(1, 1), canopyMat, T.length);
    T.forEach((p, i) => { const sc = rnd(2.6, 4.6); Pv.set(p[0], 2, p[1]); M.compose(Pv, Q, Sv.set(1,1,1)); trees.setMatrixAt(i, M); Pv.y = 4 + sc*0.8; M.compose(Pv, Q.setFromEuler(new THREE.Euler(rnd(0,.4), rnd(0,6), rnd(0,.4))), Sv.set(sc, sc*1.15, sc)); treeTops.setMatrixAt(i, M); Q.identity(); });
    trees.castShadow = treeTops.castShadow = true; treeTops.receiveShadow = true; scene.add(trees); scene.add(treeTops); }
}
export function setWaterMode(ultra){
  if (!waterGeoXY) return;
  if (waterMesh){ scene.remove(waterMesh); waterMesh.material.dispose(); waterMesh = null; }
  if (waterStd) waterStd.visible = !ultra;
  if (!ultra) return;
  texLoader.load(TEX+'waternormals.jpg', tex => { tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    waterMesh = new Water(waterGeoXY, {textureWidth:512, textureHeight:512, waterNormals:tex, sunDirection:sunDir.clone(), sunColor:0xffd0a0, waterColor:0x0f2a44, distortionScale:2.2, fog:true});
    waterMesh.rotation.x = -Math.PI/2; waterMesh.position.y = 0.3; scene.add(waterMesh); waterMesh.visible = S.world !== 'photo'; if (waterStd) waterStd.visible = false; });
}
export function buildStatic(data){
  for (const m of cityStatic){ scene.remove(m); m.geometry.dispose(); } cityStatic.length = 0;
  const roads = [], lines = [], minor = [];
  for (const r of data.roads){
    const w = ROAD_W[r.type] || 6;
    (w >= 10 ? roads : minor).push(...ribbon(r.pts, w, 0.28));
    if (w >= 12) lines.push(...ribbon(r.pts, 0.45, 0.34));
  }
  const add = (geos, mat) => { if (!geos.length) return; const g = BufferGeometryUtils.mergeGeometries(geos, false); const m = new THREE.Mesh(g, mat); m.receiveShadow = true; scene.add(m); cityStatic.push(m); };
  add(roads, new THREE.MeshStandardMaterial({color:'#2b2b31', roughness:.95}));
  add(minor, new THREE.MeshStandardMaterial({color:'#36363c', roughness:.95}));
  add(lines, new THREE.MeshStandardMaterial({color:'#8a8360', roughness:.9}));
  const flats = (polys, mat, y) => { const geos = []; for (const p of polys){ if (p.length<3) continue; const sh = new THREE.Shape(p.map(q=>new THREE.Vector2(q[0], -q[1]))); const g = new THREE.ShapeGeometry(sh); g.rotateX(-Math.PI/2); g.translate(0,y,0); geos.push(g); } add(geos, mat); };
  flats(data.parks, new THREE.MeshStandardMaterial({color:'#3f5a3a', roughness:1}), 0.22);
  { const geos = []; for (const p of data.water){ if (p.length<3) continue; const sh = new THREE.Shape(p.map(q=>new THREE.Vector2(q[0], -q[1]))); geos.push(new THREE.ShapeGeometry(sh)); }
    waterGeoXY = geos.length ? BufferGeometryUtils.mergeGeometries(geos, false) : null; waterStd = null;
    if (waterGeoXY){ waterStd = new THREE.Mesh(waterGeoXY, new THREE.MeshStandardMaterial({color:'#17304a', roughness:.08, metalness:.55, envMapIntensity:1.8})); waterStd.rotation.x = -Math.PI/2; waterStd.position.y = 0.26; waterStd.receiveShadow = true; scene.add(waterStd); cityStatic.push(waterStd); }
    if (waterMesh){ scene.remove(waterMesh); waterMesh = null; } setWaterMode(S.quality==='ultra'); }
  buildStreetFurniture(data);
}
export function clearCity(){
  for (const b of buildings){ scene.remove(b); b.geometry.dispose(); b.material[1].dispose(); }
  buildings.length = 0;
  for (const d of debris){ scene.remove(d.mesh); world.removeBody(d.body); } debris.length = 0;
  for (const s of sinking) scene.remove(s.mesh); sinking.length = 0; clearRuins(); clearDustWaves();
  for (const f of fires){ f.light.intensity = 0; } fires.length = 0;
  for (const s of scorches) scene.remove(s); scorches.length = 0; clearMarks();
  for (const b of S.beacons) scene.remove(b.mesh); S.beacons = [];
  if (props){ scene.remove(props); props.dispose(); props = null; }
  for (const h of S.helis) heliGroup.remove(h.g); S.helis = [];
  clearGems();
  S.chain = 0; S.mult = 1; $('#combo').classList.remove('on');
}

// ---- water helpers: multipolygon relations + coastline → filled polygons
export async function buildCity(data, def, yieldFn?){
  clearCity(); prepareFacades(def);
  buildStatic(data);
  const list = [...data.buildings].sort((a,b)=>b.vol-a.vol).slice(0, 720);
  let i = 0; for (const b of list){ addBuilding(b.pts, b.h, def, b.tags, b.id); if (++i % 60 === 0 && yieldFn) await yieldFn(i, list.length); }
  addRoofProps(); buildTraffic(data); rebuildLabels(); applyWorld();
  S.totalValue = buildings.reduce((a,b)=>a+b.userData.value, 0);
  minimap.dirty = true;
}
export function placeBeacons(n){
  for (const b of S.beacons) scene.remove(b.mesh); S.beacons = [];
  const cands = [...buildings].sort((a,b)=>b.userData.value-a.userData.value).slice(0, Math.max(n*4, 12));
  for (let i=0;i<n && cands.length;i++){
    const b = cands.splice((Math.random()*cands.length)|0, 1)[0];
    const u = b.userData; u.beacon = true; u.value *= 3;
    const mesh = new THREE.Mesh(new THREE.CylinderGeometry(3, 6, 900, 12, 1, true), new THREE.MeshBasicMaterial({color:new THREE.Color(2.5,1.6,0.4), transparent:true, opacity:.28, blending:THREE.AdditiveBlending, depthWrite:false, side:THREE.DoubleSide, fog:false}));
    mesh.position.set(u.center.x, u.h + 450, u.center.z); scene.add(mesh);
    S.beacons.push({b, mesh});
  }
}


// ── world mode: Photo hides the OSM visuals (they stay as the hit-test/destruction layer)
import { ground, rig } from './scene';
import { PIT_DEPTH } from '../world/tiles';
const DEBUG_OSM = new URLSearchParams(location.search).has('osm');   // ?osm=1 → wireframe OSM overlay on the photo mesh
export function applyWorld(){
  const photo = S.world === 'photo', hi = S.quality !== 'low';
  for (const b of buildings){ b.visible = !photo || DEBUG_OSM; if (photo && DEBUG_OSM) for (const m of (b.material as THREE.Material[])) (m as any).wireframe = true; }
  (scene.fog as THREE.FogExp2).density = photo ? 0.00045 : 0.00075;
  for (const m of cityStatic) m.visible = !photo;
  if (waterMesh) waterMesh.visible = !photo;
  for (const m of [lamps, lampHeads]) if (m) m.visible = hi && !photo;
  for (const m of [trees, treeTops, props]) if (m) m.visible = !photo;
  ground.visible = !photo; if (!photo) rig.ground = 0;
  document.body.classList.toggle('photo', photo);
}
// which OSM building's footprint contains this ground point (photo-mesh hits resolve to a building this way)
export function buildingAt(x: number, z: number){
  for (const b of buildings){ const u = b.userData; if (Math.abs(u.center.x-x) > u.fr || Math.abs(u.center.z-z) > u.fr) continue; if (pip([x, z], u.pts)) return b; }
  return null;
}

import { sampleGround } from '../world/tiles';
import { labelEls } from './labels';
// Drop OSM buildings that the photo world doesn't contain (not built yet at capture time, or gone since):
// the tile surface at their centroid is street-level although OSM says they stand ≥ 15 m.
export function ghostCandidates(){
  const out: any[] = []; if (S.world !== 'photo') return out;
  for (const b of buildings){
    const u = b.userData; if (u.h < 15) continue;
    let flat = true, seen = 0;
    for (const [fx, fz] of [[0,0],[-.35,-.35],[.35,-.35],[.35,.35],[-.35,.35]]){ const px = u.center.x + fx*u.size.x, pz = u.center.z + fz*u.size.z; if (!pip([px, pz], u.pts)) continue; const y = sampleGround(px, pz); if (y === null) continue; seen++; if (y > 3){ flat = false; break; } }
    if (seen && flat) out.push(b);
  }
  return out;
}
export function removeGhosts(list: any[]){
  for (const b of list){
    const i = buildings.indexOf(b); if (i < 0) continue;
    buildings.splice(i, 1); scene.remove(b); hideProps(b); b.geometry.dispose(); (b.material as THREE.Material[])[1].dispose();
    const el = labelEls.get(b); if (el){ el.remove(); labelEls.delete(b); }
  }
  if (list.length){ S.totalValue = buildings.reduce((a, b) => a + b.userData.value, 0); minimap.dirty = true; }
  return list.length;
}
export function ghostPass(){ return removeGhosts(ghostCandidates()); }
export const tallCount = () => buildings.filter(b => b.userData.h >= 15).length;
import { sinking } from './destruction';
import { clearRuins, clearDustWaves } from './ruins';

// nearest building whose footprint box is within `tol` metres of (x, z) — photo facades overhang OSM footprints
export function nearestBuilding(x: number, z: number, tol: number){
  let best = null, bd = tol;
  for (const b of buildings){ const u = b.userData; const dx = Math.max(0, Math.abs(x - u.center.x) - u.size.x/2), dz = Math.max(0, Math.abs(z - u.center.z) - u.size.z/2); const d = Math.hypot(dx, dz); if (d < bd){ bd = d; best = b; } }
  return best;
}
